/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import PersonIcon from '@mui/icons-material/Person'
import BadgeIcon from '@mui/icons-material/Badge'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SendIcon from '@mui/icons-material/Send'
import GroupsIcon from '@mui/icons-material/Groups'
import { keyframes } from '@emotion/react'
import { useNavigate, useLocation } from 'react-router-dom'
import AIService from '../../services/aiService'
import { overlapHandoff } from '../../helpers/aiHandoff'
import CopyButton from '../../components/shared/CopyButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { toCSV } from '../../utils/csv'
import { downloadFile } from '../../utils/download'
import {
    ACCENT,
    CATEGORY,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); }`

const TYPE_OPTIONS = [
    {
        value: 'profile_id',
        label: 'Profile ID',
        sub: 'Contact · Student_Lead_Id__c',
        icon: <PersonIcon sx={{ fontSize: ICON.md }} />,
        placeholder:
            'a1b2c3d4-e5f6-7890-abcd-ef1234567890\nf9e8d7c6-b5a4-3210-fedc-ba9876543210\n...',
        // Identity colours (CATEGORY): text on the card and on their own
        // 15% tint in both themes.
        color: CATEGORY.orange,
        colorLight: tint(CATEGORY.orange, 0.15),
        colorBorder: tint(CATEGORY.orange, 0.4),
    },
    {
        value: 'lead_id',
        label: 'Lead ID',
        sub: 'Lead · Lead_Id__c',
        icon: <BadgeIcon sx={{ fontSize: ICON.md }} />,
        placeholder: 'LEAD-00123\nLEAD-00456\n...',
        color: CATEGORY.cyan,
        colorLight: tint(CATEGORY.cyan, 0.15),
        colorBorder: tint(CATEGORY.cyan, 0.4),
    },
]

function TypeCard({ option, selected, onSelect }) {
    const active = selected === option.value
    return (
        <Box
            role="button"
            tabIndex={0}
            aria-pressed={active}
            aria-label={`Identity type: ${option.label}`}
            onClick={() => onSelect(option.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(option.value)
                }
            }}
            sx={{
                flex: 1,
                border: `1px solid ${
                    active ? option.colorBorder : SURFACE.border
                }`,
                background: active ? option.colorLight : SURFACE.subtle,
                borderRadius: `${RADIUS.md}px`,
                p: 1.75,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                transition: 'all 0.15s ease',
                '&:hover': {
                    borderColor: active
                        ? option.colorBorder
                        : SURFACE.borderHover,
                    background: active ? option.colorLight : SURFACE.hover,
                },
            }}
        >
            <Box
                sx={{
                    color: active ? option.color : TEXT.muted,
                    display: 'flex',
                    transition: 'color 0.15s ease',
                }}
            >
                {option.icon}
            </Box>
            <Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: active ? 700 : 500,
                        color: active ? TEXT.primary : TEXT.muted,
                        lineHeight: 1.2,
                    }}
                >
                    {option.label}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: active ? option.color : TEXT.faint,
                        mt: 0.25,
                        fontFamily: FONT.mono,
                    }}
                >
                    {option.sub}
                </Typography>
            </Box>
            {active && (
                <Box
                    sx={{
                        ml: 'auto',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: option.color,
                    }}
                />
            )}
        </Box>
    )
}

function CopyCell({ value, icon }) {
    if (!value)
        return (
            <Typography variant="caption" sx={{ color: TEXT.faint }}>
                —
            </Typography>
        )
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                minWidth: 0,
            }}
        >
            {React.cloneElement(icon, {
                sx: {
                    fontSize: ICON.sm,
                    color: TEXT.faint,
                    flexShrink: 0,
                },
            })}
            <Tooltip title={value} placement="top-start">
                <Typography
                    variant="caption"
                    sx={{
                        color: TEXT.secondary,
                        fontFamily: FONT.mono,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {value}
                </Typography>
            </Tooltip>
            <CopyButton text={value} size={12} />
        </Box>
    )
}

function ResultRow({ row, index }) {
    const found = row.status === 'found'
    const tone = found
        ? 'success'
        : row.status === 'error'
        ? 'error'
        : 'warning'
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1.4fr 1.2fr 90px',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${SURFACE.border}`,
                animation: `${fadeIn} ${0.06 + index * 0.02}s ease`,
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { background: SURFACE.cardHover },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Tooltip title={row.id} placement="top-start">
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{
                            color: TEXT.primary,
                            fontFamily: FONT.mono,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {row.id}
                    </Typography>
                </Tooltip>
                {row.name && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{
                            color: TEXT.faint,
                            mt: 0.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {row.name}
                    </Typography>
                )}
            </Box>
            <CopyCell value={row.email} icon={<EmailIcon />} />
            <CopyCell value={row.mobile} icon={<PhoneIcon />} />
            <Chip
                icon={
                    found ? (
                        <CheckCircleIcon
                            sx={{
                                fontSize: `${ICON.xs}px !important`,
                                color: `${STATUS.success.fg} !important`,
                            }}
                        />
                    ) : (
                        <ErrorOutlineIcon
                            sx={{
                                fontSize: `${ICON.xs}px !important`,
                                color: `${STATUS[tone].fg} !important`,
                            }}
                        />
                    )
                }
                label={
                    found
                        ? 'Found'
                        : row.status === 'error'
                        ? 'Error'
                        : 'Not Found'
                }
                size="small"
                sx={{
                    background: STATUS[tone].bg,
                    color: STATUS[tone].fg,
                    border: `1px solid ${STATUS[tone].border}`,
                    typography: 'caption',
                    fontWeight: 600,
                    height: 22,
                }}
            />
        </Box>
    )
}

export default function CRMEnrich() {
    const navigate = useNavigate()
    const location = useLocation()
    const [identityType, setIdentityType] = useState('profile_id')
    const [identityText, setIdentityText] = useState('')
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [filter, setFilter] = useState('all')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        const state = location.state
        if (state?.identities && Array.isArray(state.identities)) {
            setIdentityText(state.identities.join('\n'))
            if (state.identity_type) setIdentityType(state.identity_type)
            return
        }
        // Starts empty. It used to hold four made-up ids, so a Look Up right
        // away asked Salesforce for records that do not exist; the input's
        // placeholder shows what an id of the chosen type looks like.
        setIdentityText('')
    }, [location.state])

    const activeOption = TYPE_OPTIONS.find((o) => o.value === identityType)
    const lines = identityText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
    const identityCount = lines.length

    const handleLookup = async () => {
        if (!lines.length) return
        setLoading(true)
        setError('')
        setResult(null)
        try {
            const res = await AIService.crmLookup({
                identities: lines,
                identity_type: identityType,
            })
            setResult(res.data?.result)
        } catch {
            setError('CRM lookup failed. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleExport = () => {
        if (!result) return
        const found = (result.results ?? []).filter((r) => r.status === 'found')
        const csv = toCSV(
            found.map((r) => ({
                id: r.id,
                name: r.name || '',
                email: r.email || '',
                mobile: r.mobile || '',
            })),
            ['id', 'name', 'email', 'mobile']
        )
        downloadFile(csv, 'crm_resolved.csv', 'text/csv')
    }

    const handleExportToBulkSend = () => {
        if (!result) return
        const found = (result.results ?? []).filter((r) => r.status === 'found')
        const csvText = toCSV(
            found.map((r) => ({
                mobile: r.mobile || '',
                email: r.email || '',
                name: r.name || '',
            })),
            ['mobile', 'email', 'name']
        )
        navigate('/bulk-send', { state: { csvText } })
    }

    const handleAudienceOverlap = () => {
        if (!result) return
        navigate('/ai/audience-overlap', {
            state: overlapHandoff(result.results),
        })
    }

    const filteredResults = (result?.results ?? []).filter((r) =>
        filter === 'found'
            ? r.status === 'found'
            : filter === 'not_found'
            ? r.status !== 'found'
            : true
    )

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 2, md: 3 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            background: GRADIENT.brand,
                            borderRadius: `${RADIUS.md}px`,
                            p: 1,
                            display: 'flex',
                        }}
                    >
                        {/* Deep Blue on the orange-to-yellow badge in both
                            themes (Almost White on its yellow end is 1.8:1). */}
                        <ManageSearchIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="p"
                            sx={{ color: TEXT.primary }}
                        >
                            CRM Enrich
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.secondary }}
                        >
                            Resolve IDs to email &amp; mobile via Salesforce —
                            no manual lookup needed
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        // minmax(0, 1fr): a bare or implicit track grows to its
                        // widest child, which pushed a phone page 1px wide.
                        gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 2.5,
                    }}
                >
                    {/* Left: input form */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {/* Type selector */}
                        <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
                            <Typography
                                variant="overline"
                                component="p"
                                sx={{ color: TEXT.muted, mb: 1.25 }}
                            >
                                Identity Type
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {TYPE_OPTIONS.map((opt) => (
                                    <TypeCard
                                        key={opt.value}
                                        option={opt}
                                        selected={identityType}
                                        onSelect={setIdentityType}
                                    />
                                ))}
                            </Box>
                        </Paper>

                        {/* ID input */}
                        <Paper elevation={0} sx={{ ...cardSx, p: 2, flex: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.25,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    component="p"
                                    sx={{ color: TEXT.muted }}
                                >
                                    {activeOption.label}s — one per line
                                </Typography>
                                {identityCount > 0 && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: activeOption.color,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {identityCount} ID
                                        {identityCount !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Box>
                            <TextField
                                value={identityText}
                                onChange={(e) =>
                                    setIdentityText(e.target.value)
                                }
                                placeholder={activeOption.placeholder}
                                multiline
                                rows={8}
                                fullWidth
                                inputProps={{
                                    'aria-label': `${activeOption.label}s to look up`,
                                }}
                                sx={{
                                    '& textarea': {
                                        typography: 'body2',
                                        fontFamily: FONT.mono,
                                        lineHeight: 1.7,
                                    },
                                }}
                            />
                            {error && (
                                <Box sx={{ mt: 1 }}>
                                    <ErrorBanner message={error} />
                                </Box>
                            )}
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleLookup}
                                // No connection gate: the API resolves IDs
                                // with its own server-side Salesforce client.
                                // The gate called a route that does not exist
                                // (404), so the button never enabled.
                                disabled={loading || !identityCount}
                                startIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={15}
                                            sx={{ color: ON_ORANGE }}
                                        />
                                    ) : (
                                        <ManageSearchIcon
                                            sx={{
                                                fontSize: `${ICON.md}px !important`,
                                            }}
                                        />
                                    )
                                }
                                // The theme's primary button (orange, Deep
                                // Blue label, lighter on hover, its own
                                // disabled state): a label on the identity
                                // colour failed AA in one theme or the other.
                                sx={{ mt: 1.5, fontWeight: 600, py: 1.1 }}
                            >
                                {loading
                                    ? 'Searching Salesforce…'
                                    : identityCount
                                    ? `Look Up ${identityCount} ${
                                          activeOption.label
                                      }${identityCount !== 1 ? 's' : ''}`
                                    : 'Paste IDs above to begin'}
                            </Button>
                        </Paper>
                    </Box>

                    {/* Right: results or placeholder */}
                    <Box>
                        {!result && !loading && (
                            <Paper
                                elevation={0}
                                sx={{
                                    background: SURFACE.card,
                                    border: `1px dashed ${SURFACE.borderHover}`,
                                    borderRadius: `${RADIUS.lg}px`,
                                    height: '100%',
                                    minHeight: 360,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <ManageSearchIcon
                                    sx={{
                                        fontSize: ICON.hero,
                                        color: TEXT.decorative,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.faint,
                                        textAlign: 'center',
                                        maxWidth: 200,
                                    }}
                                >
                                    Resolved contacts will appear here
                                </Typography>
                            </Paper>
                        )}

                        {loading && (
                            <Paper
                                elevation={0}
                                sx={{
                                    ...cardSx,
                                    height: '100%',
                                    minHeight: 360,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                }}
                            >
                                <CircularProgress
                                    size={32}
                                    sx={{ color: activeOption.color }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ color: TEXT.muted }}
                                >
                                    Querying Salesforce…
                                </Typography>
                            </Paper>
                        )}

                        {result && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    animation: `${fadeIn} 0.25s ease`,
                                }}
                            >
                                {/* Stat row */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: 1.5,
                                    }}
                                >
                                    {[
                                        {
                                            label: 'Checked',
                                            value: result.total,
                                            color: ACCENT.text,
                                        },
                                        {
                                            label: 'Found',
                                            value: result.found,
                                            color: STATUS.success.fg,
                                        },
                                        {
                                            label: 'Not Found',
                                            value: result.not_found,
                                            color: STATUS.warning.fg,
                                        },
                                    ].map((s) => (
                                        <Paper
                                            key={s.label}
                                            elevation={0}
                                            sx={{
                                                background: SURFACE.card,
                                                border: `1px solid ${tint(
                                                    s.color,
                                                    0.13
                                                )}`,
                                                borderRadius: `${RADIUS.md}px`,
                                                p: 1.75,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant="h1"
                                                component="p"
                                                sx={{
                                                    color: s.color,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {s.value}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                component="p"
                                                sx={{
                                                    color: TEXT.secondary,
                                                    mt: 0.5,
                                                }}
                                            >
                                                {s.label}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>

                                {/* Table card */}
                                <Paper
                                    elevation={0}
                                    sx={{ ...cardSx, overflow: 'hidden' }}
                                >
                                    {/* Toolbar */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 2.5,
                                            py: 1.25,
                                            borderBottom: `1px solid ${SURFACE.border}`,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {['all', 'found', 'not_found'].map(
                                            (f) => {
                                                const count =
                                                    f === 'all'
                                                        ? result.total
                                                        : f === 'found'
                                                        ? result.found
                                                        : result.not_found
                                                const label =
                                                    f === 'all'
                                                        ? 'All'
                                                        : f === 'found'
                                                        ? 'Found'
                                                        : 'Not Found'
                                                const active = filter === f
                                                return (
                                                    <Chip
                                                        key={f}
                                                        label={`${label} · ${count}`}
                                                        size="small"
                                                        onClick={() =>
                                                            setFilter(f)
                                                        }
                                                        sx={{
                                                            cursor: 'pointer',
                                                            typography:
                                                                'caption',
                                                            height: 22,
                                                            background: active
                                                                ? ACCENT.bg15
                                                                : SURFACE.subtle,
                                                            color: active
                                                                ? ACCENT.text
                                                                : TEXT.muted,
                                                            border: `1px solid ${
                                                                active
                                                                    ? ACCENT.border55
                                                                    : SURFACE.border
                                                            }`,
                                                            fontWeight: active
                                                                ? 700
                                                                : 400,
                                                        }}
                                                    />
                                                )
                                            }
                                        )}
                                        <Box sx={{ flex: 1 }} />
                                        {result.found > 0 && (
                                            <Button
                                                size="small"
                                                startIcon={
                                                    <FileDownloadIcon
                                                        sx={{
                                                            fontSize: `${ICON.sm}px !important`,
                                                        }}
                                                    />
                                                }
                                                onClick={handleExport}
                                                sx={{
                                                    color: TEXT.secondary,
                                                    typography: 'caption',
                                                    py: 0.25,
                                                    '&:hover': {
                                                        color: TEXT.primary,
                                                        background:
                                                            SURFACE.hover,
                                                    },
                                                }}
                                            >
                                                Export
                                            </Button>
                                        )}
                                    </Box>

                                    {/* Column headers */}
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns:
                                                '1.2fr 1.4fr 1.2fr 90px',
                                            gap: 2,
                                            px: 2.5,
                                            py: 0.85,
                                            borderBottom: `1px solid ${SURFACE.border}`,
                                            background: SURFACE.sunken,
                                        }}
                                    >
                                        {[
                                            'ID',
                                            'Email',
                                            'Mobile',
                                            'Status',
                                        ].map((h) => (
                                            <Typography
                                                key={h}
                                                variant="overline"
                                                component="p"
                                                sx={{ color: TEXT.faint }}
                                            >
                                                {h}
                                            </Typography>
                                        ))}
                                    </Box>

                                    {filteredResults.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: TEXT.faint,
                                                p: 3,
                                                textAlign: 'center',
                                            }}
                                        >
                                            No results match this filter.
                                        </Typography>
                                    ) : (
                                        filteredResults.map((r, i) => (
                                            <ResultRow
                                                key={r.id}
                                                row={r}
                                                index={i}
                                            />
                                        ))
                                    )}
                                </Paper>

                                {/* Integration CTAs */}
                                {result.found > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            mt: 2,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<SendIcon />}
                                            onClick={handleExportToBulkSend}
                                        >
                                            Export to Bulk Send
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<GroupsIcon />}
                                            onClick={handleAudienceOverlap}
                                        >
                                            Check Audience Overlap
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
